import { http } from '../lib/api/http';

const ENDPOINT = '/request-proposals';
const DEFAULT_LIMIT = 60;

const DEFAULT_STATUS = 'pending';

const coerceString = value => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    const candidate = value.find(Boolean);
    return candidate ? String(candidate) : '';
  }
  return String(value || '');
};

const mapCompany = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      slug: '',
      phoneNumber: '',
      email: '',
    };
  }

  const companyRecord = Array.isArray(record) ? record[0] : record;

  return {
    id: coerceString(companyRecord?.id),
    name: coerceString(companyRecord?.name),
    slug: coerceString(companyRecord?.slug),
    phoneNumber: coerceString(
      companyRecord?.phoneNumber ||
        companyRecord?.contactPhone ||
        companyRecord?.phone ||
        companyRecord?.contactPhoneNumber
    ),
    email: coerceString(
      companyRecord?.contactEmail ||
        companyRecord?.email ||
        companyRecord?.notificationEmail
    ),
  };
};

const mapUser = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      email: '',
      phoneNumber: '',
    };
  }

  const userRecord = Array.isArray(record) ? record[0] : record;

  return {
    id: coerceString(userRecord?.id),
    name:
      coerceString(userRecord?.name) ||
      coerceString(userRecord?.username) ||
      coerceString(userRecord?.email),
    email: coerceString(userRecord?.email),
    phoneNumber: coerceString(
      userRecord?.phoneNumber ||
        userRecord?.contactPhone ||
        userRecord?.phone
    ),
  };
};

const mapProposalRecord = record => {
  if (!record) {
    return {
      id: '',
      requestId: '',
      companyId: '',
      createdBy: '',
      assetType: 'publicacion',
      assetReference: '',
      price: '',
      details: '',
      providerName: '',
      providerEmail: '',
      providerPhone: '',
      clientContactName: '',
      clientContactEmail: '',
      clientContactPhone: '',
      clientContactSharedAt: '',
      status: DEFAULT_STATUS,
      created: '',
      updated: '',
      request: null,
      company: mapCompany(null),
      creator: mapUser(null),
    };
  }

  const companyInfo = record.company || record.company_summary || null;
  const creatorInfo = record.createdBy || record.created_by || null;

  return {
    id: coerceString(record.id),
    requestId: coerceString(record.request_id || record.requestId || record.request),
    companyId: coerceString(record.company_id || record.companyId || record.company),
    createdBy: coerceString(record.created_by || record.createdBy || record.createdById),
    assetType: coerceString(record.assetType || record.asset_type || 'publicacion'),
    assetReference: coerceString(record.assetReference || record.asset_reference),
    price: coerceString(record.price || record.cost || record.amount),
    details: coerceString(record.details || record.notes || record.description),
    providerName: coerceString(record.providerName || record.contactName),
    providerEmail: coerceString(
      record.providerEmail ||
        record.contactEmail ||
        record.email
    ),
    providerPhone: coerceString(
      record.providerPhone ||
        record.contactPhone ||
        record.phone
    ),
    clientContactName: coerceString(
      record.clientContactName || record.client_contact_name
    ),
    clientContactEmail: coerceString(
      record.clientContactEmail || record.client_contact_email
    ),
    clientContactPhone: coerceString(
      record.clientContactPhone || record.client_contact_phone
    ),
    clientContactSharedAt: coerceString(
      record.clientContactSharedAt || record.client_contact_shared_at
    ),
    status: coerceString(record.status) || DEFAULT_STATUS,
    created: record.created_at || record.created || '',
    updated: record.updated_at || record.updated || '',
    request: null,
    company: mapCompany(companyInfo),
    creator: mapUser(creatorInfo),
  };
};

const buildCreatePayload = payload => {
  if (!payload) {
    throw new Error('Los datos de la propuesta son requeridos.');
  }

  const body = {};

  if (payload.requestId || payload.request) {
    body.request_id = payload.requestId || payload.request;
  }

  if (payload.companyId || payload.company) {
    body.company_id = payload.companyId || payload.company;
  }

  if (payload.assetType) {
    body.asset_type = String(payload.assetType);
  }

  if (payload.assetReference) {
    body.asset_reference = String(payload.assetReference);
  }

  if (payload.price || payload.cost) {
    body.price = String(payload.price || payload.cost);
  }

  if (payload.details || payload.notes) {
    body.details = String(payload.details || payload.notes);
  }

  if (payload.providerName || payload.contactName) {
    body.provider_name = String(payload.providerName || payload.contactName);
  }

  if (payload.providerEmail || payload.contactEmail) {
    body.provider_email = String(payload.providerEmail || payload.contactEmail);
  }

  if (payload.providerPhone || payload.contactPhone) {
    body.provider_phone = String(payload.providerPhone || payload.contactPhone);
  }

  if (payload.status) {
    body.status = String(payload.status);
  } else {
    body.status = DEFAULT_STATUS;
  }

  return body;
};

const buildUpdatePayload = payload => {
  const body = {};

  if (!payload) {
    return body;
  }

  if (payload.assetType !== undefined) {
    body.asset_type = payload.assetType ? String(payload.assetType) : null;
  }

  if (payload.assetReference !== undefined) {
    body.asset_reference = payload.assetReference ? String(payload.assetReference) : null;
  }

  if (payload.price !== undefined || payload.cost !== undefined) {
    const raw = payload.price ?? payload.cost;
    body.price = raw ? String(raw) : null;
  }

  if (payload.details !== undefined || payload.notes !== undefined) {
    const raw = payload.details ?? payload.notes;
    body.details = raw ? String(raw) : null;
  }

  if (payload.providerName !== undefined || payload.contactName !== undefined) {
    const raw = payload.providerName ?? payload.contactName;
    body.provider_name = raw ? String(raw) : null;
  }

  if (payload.providerEmail !== undefined || payload.contactEmail !== undefined) {
    const raw = payload.providerEmail ?? payload.contactEmail;
    body.provider_email = raw ? String(raw) : null;
  }

  if (payload.providerPhone !== undefined || payload.contactPhone !== undefined) {
    const raw = payload.providerPhone ?? payload.contactPhone;
    body.provider_phone = raw ? String(raw) : null;
  }

  if (payload.status !== undefined) {
    body.status = payload.status ? String(payload.status) : null;
  }

  if (payload.clientContactName !== undefined) {
    body.client_contact_name = payload.clientContactName
      ? String(payload.clientContactName)
      : null;
  }

  if (payload.clientContactEmail !== undefined) {
    body.client_contact_email = payload.clientContactEmail
      ? String(payload.clientContactEmail)
      : null;
  }

  if (payload.clientContactPhone !== undefined) {
    body.client_contact_phone = payload.clientContactPhone
      ? String(payload.clientContactPhone)
      : null;
  }

  if (payload.clientContactSharedAt !== undefined) {
    body.client_contact_shared_at = payload.clientContactSharedAt || null;
  }

  return body;
};

const handleRequestError = error => {
  if (error?.status === 404) {
    throw new Error(
      'El endpoint "/request-proposals" no está disponible. Asegúrate de que el backend esté actualizado.'
    );
  }
  throw error;
};

export async function createRequestProposal(payload) {
  const body = buildCreatePayload(payload);

  if (!body.request) {
    throw new Error('Debes indicar la solicitud a la que responde la propuesta.');
  }

  if (!body.company) {
    throw new Error('Debes asociar la propuesta con la empresa que la envía.');
  }

  if (!body.providerName) {
    throw new Error('El nombre del contacto es obligatorio.');
  }

  if (!body.providerEmail) {
    throw new Error('El correo electrónico de contacto es obligatorio.');
  }

  if (!body.providerPhone) {
    throw new Error('El teléfono de contacto es obligatorio.');
  }

  try {
    const { data } = await http.post(ENDPOINT, body);
    return mapProposalRecord(data);
  } catch (error) {
    handleRequestError(error);
    return null;
  }
}

export async function listRequestProposals(requestId, { limit = DEFAULT_LIMIT } = {}) {
  if (!requestId) {
    return [];
  }

  try {
    const { data } = await http.get(ENDPOINT, {
      params: {
        request_id: requestId,
        limit,
      },
    });
    const items = Array.isArray(data) ? data : [];
    return items.map(mapProposalRecord);
  } catch (error) {
    handleRequestError(error);
    return [];
  }
}

export async function listCompanyProposals(companyId, { limit = DEFAULT_LIMIT } = {}) {
  if (!companyId) {
    return [];
  }

  try {
    const { data } = await http.get(ENDPOINT, {
      params: {
        company_id: companyId,
        limit,
      },
    });
    const items = Array.isArray(data) ? data : [];
    return items.map(mapProposalRecord);
  } catch (error) {
    handleRequestError(error);
    return [];
  }
}

export async function getRequestProposalById(id) {
  if (!id) {
    throw new Error('El identificador de la propuesta es obligatorio.');
  }

  try {
    const { data } = await http.get(`${ENDPOINT}/${encodeURIComponent(id)}`);
    return mapProposalRecord(data);
  } catch (error) {
    handleRequestError(error);
    return null;
  }
}

export async function updateRequestProposal(id, payload) {
  if (!id) {
    throw new Error('El identificador de la propuesta es obligatorio.');
  }

  const body = buildUpdatePayload(payload);

  try {
    const { data } = await http.patch(`${ENDPOINT}/${encodeURIComponent(id)}`, body);
    return mapProposalRecord(data);
  } catch (error) {
    handleRequestError(error);
    return null;
  }
}

export async function setProposalStatus(id, status, extras = {}) {
  if (!id) {
    throw new Error('El identificador de la propuesta es obligatorio.');
  }
  if (!status) {
    throw new Error('Indica el estado a establecer.');
  }

  const body = buildUpdatePayload({ ...extras, status });

  try {
    const { data } = await http.patch(`${ENDPOINT}/${encodeURIComponent(id)}`, body);
    return mapProposalRecord(data);
  } catch (error) {
    handleRequestError(error);
    return null;
  }
}

export const PROPOSAL_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};
